import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Sparkles, BookOpen, Bell, Tag, Bug, Map, Plus, Check, Rocket, Shield,
  ChevronRight, X, Edit3, Trash2, Loader2, Calendar, AlertTriangle, GitCommitHorizontal,
  Power, LayoutDashboard, BarChart3, Users, ShoppingBag, Wrench, Package, Store,
  Heart, QrCode, Image as ImageIcon, FileText, HelpCircle, FolderOpen, MapPin,
  TrendingUp, Link2, PhoneCall, Palette, Layout, CreditCard, LifeBuoy, Settings as SettingsIcon,
  HardDrive, Smartphone, Lock, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

// Lucide icon resolver for feature lists
const LUCIDE_ICONS = {
  LayoutDashboard, BarChart3, GitCommitHorizontal, Users, ShoppingBag, Calendar,
  Wrench, Package, Store, Heart, QrCode, Image: ImageIcon, FileText, HelpCircle,
  FolderOpen, MapPin, TrendingUp, Sparkles, Link2, PhoneCall, Shield, Palette,
  Layout, CreditCard, LifeBuoy, Settings: SettingsIcon, HardDrive, Rocket, Smartphone,
};

// French labels for status / priority enums
const STATUS_LABELS = {
  open: "Ouvert", in_progress: "En cours", resolved: "Résolu",
  planned: "Planifié", released: "Livré", cancelled: "Annulé",
};
const PRIORITY_LABELS = {
  low: "Faible", medium: "Moyenne", high: "Haute", critical: "Critique",
};

const TABS_CLIENT = [
  { key: "changelog", label: "Quoi de neuf", icon: Sparkles },
  { key: "features", label: "Fonctionnalités actives", icon: Power },
  { key: "docs", label: "Documentation", icon: BookOpen },
];
const TABS_ADMIN = [
  ...TABS_CLIENT,
  { key: "releases", label: "Versions", icon: Tag, admin: true },
  { key: "bugs", label: "Bugs", icon: Bug, admin: true },
  { key: "roadmap", label: "Roadmap", icon: Map, admin: true },
];

const EMPTY_RELEASE = {
  version: "", title: "", summary: "", released_at: new Date().toISOString(),
  features: [], improvements: [], fixes: [], security: [],
  visible_to_client: true, is_current: false,
};
const EMPTY_BUG = { title: "", description: "", status: "open", priority: "medium", resolved_in_version: "" };
const EMPTY_ROADMAP = { title: "", description: "", status: "planned", priority: "P1", target_version: "", category: "" };
const EMPTY_DOC = { slug: "", title: "", category: "general", content: "", video_url: "", video_steps: [], order: 100, visible_to_client: true };

export default function AgencyCockpitAdmin() {
  // Sur l'espace agence on est forcément admin (gating layout).
  const isAdmin = true;
  const TABS = TABS_ADMIN;
  const [tab, setTab] = useState("changelog");
  const [meta, setMeta] = useState(null);

  const loadMeta = async () => {
    try { const { data } = await api.get("/cockpit/meta"); setMeta(data); } catch {}
  };
  useEffect(() => { loadMeta(); }, []);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-cockpit-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Shield className="w-3.5 h-3.5" /> ESPACE AGENCE · COCKPIT (Admin)
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">
            Pilotage produit COCKPIT
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Centre de contrôle agence : publier des versions, suivre les bugs, gérer la roadmap et la documentation diffusée aux clients.
          </p>
        </div>
        {meta && (
          <div className="flex flex-col items-end gap-1.5">
            <Badge className="bg-gradient-to-r from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white border-0 text-xs font-bold px-3 py-1.5">
              <Rocket className="w-3 h-3 mr-1" /> Version actuelle · v{meta.version}
            </Badge>
            {meta.unread_count > 0 && (
              <Badge className="bg-red-500 text-white border-0 text-[10px] uppercase tracking-wider animate-pulse">
                {meta.unread_count} nouveauté{meta.unread_count > 1 ? "s" : ""} à lire
              </Badge>
            )}
          </div>
        )}
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[hsl(var(--border))] overflow-x-auto" data-testid="cockpit-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
              tab === t.key
                ? "border-[color:var(--brand-sky)] text-[color:var(--brand-navy)]"
                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"
            }`}
            data-testid={`cockpit-tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.admin && <Shield className="w-3 h-3 text-amber-500" />}
          </button>
        ))}
      </div>

      {/* Panels */}
      {tab === "changelog" && <ChangelogPanel meta={meta} onRead={loadMeta} />}
      {tab === "features" && <FeaturesPanel />}
      {tab === "docs" && <DocsPanel isAdmin={isAdmin} />}
      {tab === "releases" && isAdmin && <ReleasesManagePanel onMutate={loadMeta} />}
      {tab === "bugs" && isAdmin && <BugsPanel />}
      {tab === "roadmap" && isAdmin && <RoadmapPanel />}
    </div>
  );
}

// ==================== CHANGELOG (public) ====================
function ChangelogPanel({ meta, onRead }) {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/cockpit/releases"); setReleases(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async () => {
    if (!meta?.latest_release) return;
    try {
      await api.post("/cockpit/mark-read", { version: meta.latest_release.version });
      toast.success("Marqué comme lu");
      onRead && onRead();
    } catch {}
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!releases.length) return <EmptyState icon={Sparkles} title="Aucune version publiée" />;

  return (
    <div className="space-y-6">
      {meta?.unread_count > 0 && (
        <Card className="p-4 bg-gradient-to-br from-[color:var(--brand-sky)]/10 to-[color:var(--brand-navy)]/5 border-[color:var(--brand-sky)]/40 flex items-center justify-between gap-3 flex-wrap" data-testid="unread-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sky)]/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[color:var(--brand-sky)]" />
            </div>
            <div>
              <div className="font-bold text-[color:var(--brand-navy)]">
                {meta.unread_count} nouveauté{meta.unread_count > 1 ? "s" : ""} disponible{meta.unread_count > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Découvrez les dernières améliorations du COCKPIT</div>
            </div>
          </div>
          <Button size="sm" onClick={markRead} className="bg-[color:var(--brand-navy)]" data-testid="mark-read-btn">
            <Check className="w-3.5 h-3.5 mr-1.5" /> Tout marquer comme lu
          </Button>
        </Card>
      )}

      <div className="space-y-4">
        {releases.map((r, idx) => (
          <Card key={r.id} className={`p-5 ${r.is_current ? "border-2 border-[color:var(--brand-sky)]" : ""}`} data-testid={`release-${r.version}`}>
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[color:var(--brand-navy)] text-white border-0 text-sm font-mono font-bold">v{r.version}</Badge>
                  {r.is_current && <Badge className="bg-emerald-500 text-white border-0 text-[10px] uppercase tracking-wider">Actuelle</Badge>}
                  {idx === 0 && !r.is_current && <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Latest</Badge>}
                </div>
                <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mt-2">{r.title}</h3>
                <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(r.released_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                </div>
              </div>
            </div>
            {r.summary && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{r.summary}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {r.features?.length > 0 && <Section title="✨ Nouveautés" items={r.features} color="text-emerald-700 bg-emerald-50" />}
              {r.improvements?.length > 0 && <Section title="⬆ Améliorations" items={r.improvements} color="text-blue-700 bg-blue-50" />}
              {r.fixes?.length > 0 && <Section title="🐛 Corrections" items={r.fixes} color="text-amber-700 bg-amber-50" />}
              {r.security?.length > 0 && <Section title="🔒 Sécurité" items={r.security} color="text-red-700 bg-red-50" />}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Section({ title, items, color }) {
  return (
    <div className={`rounded-lg p-3 ${color}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1.5">{title}</div>
      <ul className="text-xs space-y-1">
        {items.map((it, i) => <li key={i} className="flex gap-1.5"><span className="shrink-0">·</span><span>{it}</span></li>)}
      </ul>
    </div>
  );
}

// ==================== DOCS ====================
function DocsPanel({ isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_DOC);

  const load = async () => {
    const { data } = await api.get("/cockpit/docs");
    setDocs(data);
    if (data.length && !selected) setSelected(data[0].slug);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/cockpit/docs/${selected}`).then(r => { setContent(r.data); setForm(r.data); setEditing(false); }).catch(() => setContent(null));
  }, [selected]);

  const groupedDocs = useMemo(() => {
    const g = {};
    docs.forEach(d => { (g[d.category] = g[d.category] || []).push(d); });
    return g;
  }, [docs]);

  const save = async () => {
    try {
      if (content?.id) {
        await api.put(`/cockpit/docs/${content.id}`, form);
      } else {
        await api.post("/cockpit/docs", form);
      }
      toast.success("Document enregistré");
      await load();
      setSelected(form.slug);
      setEditing(false);
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
  };

  const newDoc = () => {
    setContent(null); setForm({ ...EMPTY_DOC }); setEditing(true); setSelected(null);
  };

  const del = async () => {
    if (!content?.id || !window.confirm("Supprimer ce document ?")) return;
    await api.delete(`/cockpit/docs/${content.id}`);
    toast.success("Supprimé"); await load();
    setSelected(docs[0]?.slug || null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5" data-testid="cockpit-docs-panel">
      {/* Sidebar docs */}
      <Card className="p-4 h-fit sticky top-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--brand-navy)]">Sommaire</div>
          {isAdmin && <Button size="sm" variant="ghost" onClick={newDoc} className="h-7 w-7 p-0"><Plus className="w-3.5 h-3.5" /></Button>}
        </div>
        <div className="space-y-3">
          {Object.entries(groupedDocs).map(([cat, items]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1 font-bold">{cat}</div>
              <div className="space-y-0.5">
                {items.sort((a, b) => a.order - b.order).map(d => (
                  <button
                    key={d.id}
                    onClick={() => setSelected(d.slug)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition ${
                      selected === d.slug
                        ? "bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-navy)] font-bold"
                        : "hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]"
                    }`}
                    data-testid={`doc-nav-${d.slug}`}
                  >
                    {d.title}
                    {!d.visible_to_client && <Shield className="w-3 h-3 inline ml-1 text-amber-500" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Content */}
      <Card className="p-6 min-h-[400px]">
        {editing && isAdmin ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-bold text-lg text-[color:var(--brand-navy)]">{content ? "Modifier le document" : "Nouveau document"}</h2>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm(content || EMPTY_DOC); }}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} data-testid="doc-slug" /></div>
              <div><Label className="text-xs">Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="doc-title" /></div>
              <div><Label className="text-xs">Catégorie</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label className="text-xs">Ordre</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: +e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.visible_to_client} onCheckedChange={v => setForm({ ...form, visible_to_client: v })} /><Label className="text-xs">Visible par le client</Label></div>
            <div>
              <Label className="text-xs">Vidéo principale (URL YouTube embed)</Label>
              <Input
                value={form.video_url || ""}
                onChange={e => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://www.youtube.com/embed/VIDEO_ID"
                data-testid="doc-video-url"
              />
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">Format embed : <code>youtube.com/embed/ID</code> (pas watch?v=)</p>
            </div>
            <div>
              <Label className="text-xs">Tutoriels par étape (JSON array : [{"{"}"title","youtube_url","description","duration"{"}"}])</Label>
              <Textarea
                rows={5}
                value={JSON.stringify(form.video_steps || [], null, 2)}
                onChange={e => {
                  try { setForm({ ...form, video_steps: JSON.parse(e.target.value || "[]") }); } catch { /* invalid JSON, keep */ }
                }}
                className="font-mono text-xs"
                placeholder='[{"title":"Étape 1","youtube_url":"...","description":"...","duration":"2:15"}]'
                data-testid="doc-video-steps"
              />
            </div>
            <div><Label className="text-xs">Contenu (Markdown)</Label><Textarea rows={14} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="font-mono text-xs" data-testid="doc-content" /></div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              {content?.id && <Button variant="outline" onClick={del} className="text-red-600"><Trash2 className="w-4 h-4 mr-1" />Supprimer</Button>}
              <Button onClick={save} className="bg-[color:var(--brand-navy)]"><Check className="w-4 h-4 mr-1" />Enregistrer</Button>
            </div>
          </div>
        ) : content ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">{content.title}</h2>
              {isAdmin && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="doc-edit-btn"><Edit3 className="w-3.5 h-3.5 mr-1" />Modifier</Button>}
            </div>

            {/* Main video (YouTube embed) */}
            {content.video_url && (
              <div className="mb-6" data-testid="doc-main-video">
                <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
                  <iframe
                    src={content.video_url}
                    title={content.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Step-by-step video tutorial */}
            {content.video_steps?.length > 0 && (
              <div className="mb-6" data-testid="doc-video-steps">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-3">
                  Tutoriels vidéo étape par étape
                </div>
                <div className="space-y-3">
                  {content.video_steps.map((step, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 p-3 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]">
                      <div className="aspect-video rounded-lg overflow-hidden bg-black">
                        <iframe src={step.youtube_url} title={step.title} className="w-full h-full" frameBorder="0" allow="encrypted-media; picture-in-picture" allowFullScreen />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[color:var(--brand-sky)] text-white text-[10px] font-bold">{i + 1}</span>
                          <span className="font-bold text-[color:var(--brand-navy)] text-sm">{step.title}</span>
                          {step.duration && <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">· {step.duration}</span>}
                        </div>
                        {step.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{step.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Markdown text={content.content || ""} />
            <div className="mt-6 pt-4 border-t text-[11px] text-[hsl(var(--muted-foreground))]">
              Dernière mise à jour : {new Date(content.updated_at).toLocaleDateString("fr-FR")}
            </div>
          </div>
        ) : (
          <EmptyState icon={BookOpen} title="Sélectionnez un document" />
        )}
      </Card>
    </div>
  );
}

// Simple Markdown renderer (headings, bold, lists)
function Markdown({ text }) {
  const html = useMemo(() => {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/^### (.*$)/gm, '<h3 class="font-bold text-base text-[color:var(--brand-navy)] mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="font-bold text-lg text-[color:var(--brand-navy)] mt-6 mb-2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="font-display font-bold text-xl text-[color:var(--brand-navy)] mt-2 mb-3">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 list-decimal">$2</li>')
      .replace(/\n\n/g, '</p><p class="my-2">')
      .replace(/`([^`]+)`/g, '<code class="bg-[hsl(var(--muted))] px-1 rounded text-xs font-mono">$1</code>');
  }, [text]);
  return <div className="prose prose-sm max-w-none text-[hsl(var(--foreground))] leading-relaxed" dangerouslySetInnerHTML={{ __html: `<p class="my-2">${html}</p>` }} />;
}

// ==================== RELEASES MANAGEMENT (admin only) ====================
function ReleasesManagePanel({ onMutate }) {
  const [releases, setReleases] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RELEASE);

  const load = async () => { const { data } = await api.get("/cockpit/releases"); setReleases(data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm({ ...EMPTY_RELEASE, released_at: new Date().toISOString() }); };
  const startEdit = (r) => { setEditing(r.id); setForm({ ...r }); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/cockpit/releases", form);
      else await api.put(`/cockpit/releases/${editing}`, form);
      toast.success("Version enregistrée"); await load(); onMutate && onMutate(); setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette version ?")) return;
    await api.delete(`/cockpit/releases/${id}`);
    toast.success("Supprimé"); await load(); onMutate && onMutate();
  };

  if (editing) {
    return <ReleaseForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} />;
  }

  return (
    <div className="space-y-3" data-testid="releases-manage">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[hsl(var(--muted-foreground))]">{releases.length} version{releases.length > 1 ? "s" : ""} publiée{releases.length > 1 ? "s" : ""}</div>
        <Button onClick={startNew} className="bg-[color:var(--brand-navy)]" data-testid="new-release-btn"><Plus className="w-4 h-4 mr-1" />Nouvelle version</Button>
      </div>
      {releases.map(r => (
        <Card key={r.id} className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Badge className="bg-[color:var(--brand-navy)] text-white border-0 font-mono">v{r.version}</Badge>
            <div>
              <div className="font-semibold text-[color:var(--brand-navy)]">{r.title}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                {new Date(r.released_at).toLocaleDateString("fr-FR")} ·
                {r.visible_to_client ? " Client ✓" : " Agence uniquement"} ·
                {r.is_current ? " Actuelle" : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => startEdit(r)}><Edit3 className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => del(r.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ReleaseForm({ form, setForm, onSave, onCancel }) {
  const listField = (k, label) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Textarea
        rows={4}
        value={(form[k] || []).join("\n")}
        onChange={e => setForm({ ...form, [k]: e.target.value.split("\n").filter(Boolean) })}
        placeholder="Une ligne par élément"
        data-testid={`release-${k}`}
      />
    </div>
  );
  return (
    <Card className="p-6 space-y-3" data-testid="release-form">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-bold text-lg text-[color:var(--brand-navy)]">Publier une version</h2>
        <Button size="sm" variant="ghost" onClick={onCancel}><X className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Version (semver)</Label><Input value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} placeholder="1.2.0" data-testid="release-version" /></div>
        <div><Label className="text-xs">Date</Label><Input type="date" value={(form.released_at || "").slice(0, 10)} onChange={e => setForm({ ...form, released_at: new Date(e.target.value).toISOString() })} /></div>
      </div>
      <div><Label className="text-xs">Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="release-title" /></div>
      <div><Label className="text-xs">Résumé</Label><Textarea rows={2} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {listField("features", "Nouveautés")}
        {listField("improvements", "Améliorations")}
        {listField("fixes", "Corrections")}
        {listField("security", "Sécurité")}
      </div>
      <div className="flex items-center gap-4 flex-wrap pt-2 border-t">
        <div className="flex items-center gap-2"><Switch checked={form.visible_to_client} onCheckedChange={v => setForm({ ...form, visible_to_client: v })} data-testid="release-visible" /><Label className="text-xs">Visible par le client</Label></div>
        <div className="flex items-center gap-2"><Switch checked={form.is_current} onCheckedChange={v => setForm({ ...form, is_current: v })} data-testid="release-current" /><Label className="text-xs">Version actuelle (épinglée)</Label></div>
      </div>
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onCancel}>Annuler</Button><Button onClick={onSave} className="bg-[color:var(--brand-navy)]" data-testid="release-save"><Check className="w-4 h-4 mr-1" />Publier</Button></div>
    </Card>
  );
}

// ==================== BUGS ====================
function BugsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BUG);

  const load = async () => { const { data } = await api.get("/cockpit/bugs"); setItems(data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm({ ...EMPTY_BUG }); };
  const save = async () => {
    try {
      if (editing === "new") await api.post("/cockpit/bugs", form);
      else await api.put(`/cockpit/bugs/${editing}`, form);
      toast.success("Enregistré"); await load(); setEditing(null);
    } catch (e) { toast.error("Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/cockpit/bugs/${id}`); await load();
  };

  const statusColor = { open: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700", resolved: "bg-emerald-100 text-emerald-700" };
  const priorityColor = { low: "text-slate-500", medium: "text-blue-600", high: "text-amber-600", critical: "text-red-600" };

  if (editing) {
    return (
      <Card className="p-6 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-bold text-lg text-[color:var(--brand-navy)]">{editing === "new" ? "Nouveau bug" : "Modifier bug"}</h2>
          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
        </div>
        <div><Label className="text-xs">Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="bug-title" /></div>
        <div><Label className="text-xs">Description</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Statut</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="bug-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Ouvert</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="resolved">Résolu</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Priorité</Label>
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Faible</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="high">Haute</SelectItem>
                <SelectItem value="critical">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Résolu dans version</Label><Input value={form.resolved_in_version} onChange={e => setForm({ ...form, resolved_in_version: e.target.value })} placeholder="1.1.1" /></div>
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button><Button onClick={save} className="bg-[color:var(--brand-navy)]"><Check className="w-4 h-4 mr-1" />Enregistrer</Button></div>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="bugs-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[hsl(var(--muted-foreground))]">{items.length} bug{items.length > 1 ? "s" : ""} · zone interne agence</div>
        <Button onClick={startNew} className="bg-[color:var(--brand-navy)]" data-testid="new-bug-btn"><Plus className="w-4 h-4 mr-1" />Signaler un bug</Button>
      </div>
      {items.length === 0 ? <EmptyState icon={Bug} title="Aucun bug enregistré — tout va bien !" /> : items.map(b => (
        <Card key={b.id} className="p-4 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${priorityColor[b.priority] || ""}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[color:var(--brand-navy)]">{b.title}</span>
                <Badge className={`text-[9px] uppercase tracking-wider border-0 ${statusColor[b.status] || ""}`}>{STATUS_LABELS[b.status] || b.status}</Badge>
                <Badge variant="outline" className="text-[9px] uppercase">{PRIORITY_LABELS[b.priority] || b.priority}</Badge>
                {b.resolved_in_version && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">v{b.resolved_in_version}</Badge>}
              </div>
              {b.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{b.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => { setEditing(b.id); setForm({ ...b }); }}><Edit3 className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => del(b.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ==================== ROADMAP ====================
function RoadmapPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ROADMAP);

  const load = async () => { const { data } = await api.get("/cockpit/roadmap"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") await api.post("/cockpit/roadmap", form);
      else await api.put(`/cockpit/roadmap/${editing}`, form);
      toast.success("Enregistré"); await load(); setEditing(null);
    } catch { toast.error("Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/cockpit/roadmap/${id}`); await load();
  };

  const grouped = useMemo(() => {
    const g = { P0: [], P1: [], P2: [], P3: [] };
    items.forEach(i => { (g[i.priority] = g[i.priority] || []).push(i); });
    return g;
  }, [items]);

  const statusBadge = { planned: "bg-slate-100 text-slate-700", in_progress: "bg-blue-100 text-blue-700", released: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };

  if (editing) {
    return (
      <Card className="p-6 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-bold text-lg text-[color:var(--brand-navy)]">{editing === "new" ? "Nouvelle tâche" : "Modifier tâche"}</h2>
          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
        </div>
        <div><Label className="text-xs">Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="roadmap-title" /></div>
        <div><Label className="text-xs">Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Priorité</Label>
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["P0", "P1", "P2", "P3"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Statut</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Planifié</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="released">Livré</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Version cible</Label><Input value={form.target_version} onChange={e => setForm({ ...form, target_version: e.target.value })} placeholder="1.2.0" /></div>
          <div><Label className="text-xs">Catégorie</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button><Button onClick={save} className="bg-[color:var(--brand-navy)]"><Check className="w-4 h-4 mr-1" />Enregistrer</Button></div>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="roadmap-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[hsl(var(--muted-foreground))]">Feuille de route du COCKPIT</div>
        <Button onClick={() => { setEditing("new"); setForm({ ...EMPTY_ROADMAP }); }} className="bg-[color:var(--brand-navy)]"><Plus className="w-4 h-4 mr-1" />Nouvelle tâche</Button>
      </div>
      {["P0", "P1", "P2", "P3"].map(pri => grouped[pri]?.length > 0 && (
        <div key={pri}>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`text-xs font-bold ${pri === "P0" ? "bg-red-600" : pri === "P1" ? "bg-orange-500" : pri === "P2" ? "bg-amber-400" : "bg-slate-400"} text-white border-0`}>{pri}</Badge>
            <span className="text-xs font-bold text-[color:var(--brand-navy)] uppercase tracking-wider">Priorité {pri}</span>
          </div>
          <div className="space-y-2">
            {grouped[pri].map(r => (
              <Card key={r.id} className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[color:var(--brand-navy)]">{r.title}</span>
                    <Badge className={`text-[9px] uppercase border-0 ${statusBadge[r.status]}`}>{STATUS_LABELS[r.status] || r.status}</Badge>
                    {r.target_version && <Badge variant="outline" className="text-[9px]">→ v{r.target_version}</Badge>}
                    {r.category && <Badge variant="outline" className="text-[9px]">{r.category}</Badge>}
                  </div>
                  {r.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{r.description}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(r.id); setForm({ ...r }); }}><Edit3 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(r.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title }) {
  return (
    <div className="py-16 text-center">
      <Icon className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-2" />
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{title}</div>
    </div>
  );
}

// ==================== FEATURES (ACTIVE INVENTORY) ====================
function FeaturesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cockpit/features").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return <EmptyState icon={Power} title="Impossible de charger les fonctionnalités" />;

  return (
    <div className="space-y-5" data-testid="features-panel">
      {/* Summary header */}
      <Card className="p-5 bg-gradient-to-br from-[color:var(--brand-sky)]/10 to-[color:var(--brand-navy)]/5 border-[color:var(--brand-sky)]/30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1 flex items-center gap-1.5">
              <Power className="w-3 h-3" /> Inventaire des modules
            </div>
            <div className="font-display text-xl font-bold text-[color:var(--brand-navy)]">
              {data.active_features} fonctionnalité{data.active_features > 1 ? "s" : ""} active{data.active_features > 1 ? "s" : ""} sur {data.total_features}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              Activez ou désactivez les modules dans leurs pages respectives
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-16 h-16 shrink-0">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeWidth="3" className="text-[hsl(var(--muted))]" />
                <circle
                  cx="18" cy="18" r="16" fill="none" strokeWidth="3" strokeLinecap="round"
                  stroke="url(#grad)"
                  strokeDasharray={`${data.activation_rate}, 100`}
                />
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--brand-sky)" />
                    <stop offset="100%" stopColor="var(--brand-navy)" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[color:var(--brand-navy)]">
                {data.activation_rate}%
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Families grid */}
      <div className="space-y-6">
        {data.families.map(family => (
          <div key={family.name}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full" style={{ background: family.color, boxShadow: `0 0 8px ${family.color}` }} />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: family.color }}>{family.name}</span>
              {family.admin_only && <Shield className="w-3 h-3 text-amber-500" />}
              <span className="ml-auto text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                {family.features.filter(f => f.enabled).length} / {family.features.length} actives
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {family.features.map(feat => <FeatureCard key={feat.key} feat={feat} color={family.color} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ feat, color }) {
  const IconC = LUCIDE_ICONS[feat.icon] || Sparkles;
  const isOn = !!feat.enabled;
  const isLocked = !!feat.locked;
  return (
    <div
      className={`relative rounded-xl border p-3.5 transition-all ${
        isOn
          ? "bg-white border-[hsl(var(--border))] hover:shadow-md"
          : "bg-[hsl(var(--muted))]/30 border-dashed border-[hsl(var(--border))] opacity-70 hover:opacity-100"
      }`}
      data-testid={`feature-card-${feat.key}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            isOn ? "text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
          }`}
          style={isOn ? { background: color, boxShadow: `0 4px 12px ${color}40` } : {}}
        >
          <IconC className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-[color:var(--brand-navy)]">{feat.label}</span>
            {isLocked && <Lock className="w-2.5 h-2.5 text-slate-400" title="Toujours actif — module cœur" />}
          </div>
          {feat.description && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{feat.description}</p>}
          {feat.stats && <div className="text-[10px] font-mono text-[color:var(--brand-sky)] mt-1">{feat.stats}</div>}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <Badge className={`text-[9px] uppercase tracking-wider border-0 ${
            isOn
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-500"
          }`}>
            {isOn ? "Actif" : "Inactif"}
          </Badge>
          {(feat.route || feat.toggle_route) && (
            <NavLink
              to={feat.toggle_route || feat.route}
              className="text-[10px] text-[color:var(--brand-sky)] hover:underline flex items-center gap-0.5 font-semibold"
              data-testid={`feature-link-${feat.key}`}
            >
              {feat.toggle_route && !isLocked ? "Configurer" : "Ouvrir"} <ArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          )}
        </div>
      </div>
    </div>
  );
}
